import type { RenderParams } from '@seek/forms-ui';
import { useAccountContext } from '@seek/hirer-graphql-react';
import {
  required,
  badWords,
  disallowHtml,
  isEmail,
  maxLength,
} from '@seek/validators-js';
import {
  Badge,
  Button,
  Box,
  Stack,
  TextField,
  Heading,
  Text,
  Actions,
  Alert,
} from 'braid-design-system';
import React, { useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { logHandler } from 'src/shared/helpers';
import { useConfig, useTracking } from 'src/shared/hooks/';

import {
  useField,
  type FormValues,
} from '../CompanyProfilesEnquiryForm/CompanyProfilesEnquiryForm';

import * as styles from '../../../../../contactus/App/components/EnquiryForm/components/Form/Form.css';

interface FormProps {
  handleSubmit: (event: any) => any;
  setFieldValue: RenderParams<FormValues>['setFieldValue'];
  setSubmitted: (submitted: boolean) => void;
  resetForm: () => void;
}
const RECAPTCHA_KEY = {
  prod: '6LdwAKYUAAAAAGN9Svq7w2B0pOZWMrWbYD3GeQ8a',
  dev: '6LcJijEgAAAAAJGQWVLC6f9sNDz8rNYTM-7ds3j2',
};

const Form = ({
  handleSubmit,
  setFieldValue,
  setSubmitted,
  resetForm,
}: FormProps) => {
  const [invalidResponse, setInvalidResponse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const { trackEvent } = useTracking();
  const { accountContext } = useAccountContext();

  const { environment, locale } = useConfig();

  useEffect(() => {
    setFieldValue(
      'accountNumber',
      accountContext?.currentAdvertiser?.billingId || '',
    );
  }, [accountContext?.currentAdvertiser?.billingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFieldValue('companyName', accountContext?.currentAdvertiser?.name || '');
  }, [accountContext?.currentAdvertiser?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const postUrl =
    environment === 'production'
      ? 'https://wpt-contact-api.cloud.seek.com.au/profile-enquiry'
      : 'https://wpt-contact-api.staging.cloud.seek.com.au/profile-enquiry';

  const onSubmit = handleSubmit(async (formValues: any) => {
    const data = {
      ...formValues,
      siteCountry: locale,
      recaptcha,
    };
    if (!recaptcha && environment === 'development') {
      logHandler([`Form data:`, data]);
      setSubmitting(false);
      setInvalidResponse(false);
      setSubmitted(true);
    }
    if (recaptcha) {
      try {
        const response = await fetch(postUrl, {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        if (!response.ok) {
          setSubmitting(false);
          setInvalidResponse(true);
          throw response;
        }
        trackEvent('company-profiles-enquiry-form-submit');
        resetForm();
        setSubmitting(false);
        setInvalidResponse(false);
        setSubmitted(true);
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        setSubmitting(false);
        setInvalidResponse(true);
      }
    }
  });

  const name = useField({
    id: 'name',
    validators: [required, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });

  const email = useField({
    id: 'email',
    validators: [required, disallowHtml, isEmail, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const companyName = useField({
    id: 'companyName',
    validators: [required, disallowHtml, maxLength] as any,
    validatorProps: { max: 80 },
  });
  const accountNumber = useField({
    id: 'accountNumber',
    validators: [required, badWords, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack space="large">
        <Box maxWidth="small">
          <Stack space="gutter">
            <Heading level="3">About you</Heading>
            <Text>
              This information will be used by our team to get in contact with
              you.
            </Text>
            <TextField
              id="name"
              label="Your name"
              onClear={() => setFieldValue('name', '')}
              message={!name.valid && name.errorMessage}
              tone={!name.valid ? 'critical' : 'neutral'}
              {...name}
            />
            <TextField
              id="email"
              label="Your email"
              onClear={() => setFieldValue('email', '')}
              message={!email.valid && email.errorMessage}
              tone={!email.valid ? 'critical' : 'neutral'}
              {...email}
            />
          </Stack>
        </Box>
        <Box maxWidth="small">
          <Stack space="gutter">
            <Heading level="3">About your company</Heading>
            <Text>
              This information will be used to verify your company&apos;s
              account.
            </Text>
            <TextField
              id="companyName"
              label="Company Name"
              onClear={() => setFieldValue('companyName', '')}
              message={!companyName.valid && companyName.errorMessage}
              tone={!companyName.valid ? 'critical' : 'neutral'}
              {...companyName}
            />
            <TextField
              id="accountNumber"
              label="Account Number"
              onClear={() => setFieldValue('accountNumber', '')}
              message={!accountNumber.valid && accountNumber.errorMessage}
              tone={!accountNumber.valid ? 'critical' : 'neutral'}
              {...accountNumber}
            />
          </Stack>
        </Box>
        <Box className={styles.captchaContainer}>
          <ReCAPTCHA
            sitekey={
              environment === 'development'
                ? RECAPTCHA_KEY.dev
                : RECAPTCHA_KEY.prod
            }
            onChange={(val: any) => setRecaptcha(val)}
          />
        </Box>
        {invalidResponse && (
          <Alert tone="critical">
            <Text id="api-failure-text">
              There was a problem submitting your request. Please try again.
            </Text>
          </Alert>
        )}
        <Actions>
          <Button type="submit" loading={submitting} tone="formAccent">
            {submitting ? 'Submitting' : 'Submit'}
          </Button>
        </Actions>
        {environment !== 'production' && (
          <Box onClick={prefillForm(setFieldValue)} id="prefill-form">
            <Badge>Prefill form (testing only)</Badge>
          </Box>
        )}
      </Stack>
    </form>
  );
};

const prefillForm = (setFieldValue: FormProps['setFieldValue']) => () => {
  setFieldValue('name', 'Vishnu');
  setFieldValue('email', 'vishnu@test.com');
  setFieldValue('companyName', 'testCompanyName');
  setFieldValue('accountNumber', 'testAccountNumber');
};

export default Form;
